import React, { useState } from 'react';
import { StudioSettingsDialog } from './StudioSettingsDialog';
import { StudioAiBuildDialog } from './StudioAiBuildDialog';
import { useStudioEditorContext } from '../StudioEditorContext';
import { resolveSubjectContext } from '../studioActivityGeneration';

interface StudioLiveShellProps {
  error?: string | null;
  onSave: () => void;
  /** Contenido del modal de ajustes (asignatura, timers, etc.) */
  settingsContent?: React.ReactNode;
  settingsTitle?: string;
  settingsDescription?: string;
  /** @deprecated Usar settingsContent — compatibilidad con editores existentes */
  subjectTag?: string;
  /** @deprecated Usar settingsContent */
  onSubjectTagChange?: (v: string) => void;
  /** Etiqueta para el diálogo de IA */
  aiActivityLabel?: string;
  onAiBuild?: (prompt: string) => void;
  aiLoading?: boolean;
  /** Panel lateral opcional — oculto por defecto en editores con andamiaje interno */
  sidebar?: React.ReactNode;
  sidebarTitle?: string;
  sidebarMinimal?: boolean;
  toolbarExtra?: React.ReactNode;
  /** Modo embebido en modal flash (consola en vivo) */
  embedded?: boolean;
  /** Lienzo 70% + panel 30% para editores visuales */
  layout?: 'default' | 'split';
  children: React.ReactNode;
}

export function StudioLiveShell({
  error,
  onSave,
  settingsContent,
  settingsTitle,
  settingsDescription,
  subjectTag,
  onSubjectTagChange,
  aiActivityLabel,
  onAiBuild,
  aiLoading = false,
  sidebar,
  sidebarTitle = 'Recursos',
  sidebarMinimal = false,
  toolbarExtra,
  embedded = false,
  layout = 'default',
  children,
}: StudioLiveShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(sidebarMinimal);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const editorCtx = useStudioEditorContext();
  const subjectContext = resolveSubjectContext({
    subjectId: editorCtx?.subjectId,
    subjectTag: subjectTag?.trim() || undefined,
  });
  const canUseSubjectAi = Boolean(subjectContext);

  const handleAiConfirm = (prompt: string) => {
    if (!onAiBuild || !canUseSubjectAi) return;
    const wrapped = editorCtx?.wrapAiPrompt(prompt) ?? prompt;
    onAiBuild(wrapped);
  };

  const resolvedSettings =
    settingsContent ??
    (onSubjectTagChange ? (
      <label className="block">
        <span className="sala-label">Asignatura / etiqueta</span>
        <input
          className="sala-input-dark mt-2 w-full text-sm"
          value={subjectTag ?? ''}
          onChange={(e) => onSubjectTagChange(e.target.value)}
          placeholder="historia, música, matemática…"
        />
      </label>
    ) : null);

  return (
    <div
      className={`studio-live-root flex flex-col gap-2${embedded ? ' studio-live-root--embedded h-full min-h-0' : ' h-[calc(100dvh-10rem)] min-h-[420px]'}`}
    >
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {resolvedSettings && (
          <button
            type="button"
            className="sala-btn-outline text-xs tracking-wide"
            onClick={() => setSettingsOpen(true)}
          >
            Ajustes
          </button>
        )}
        {onAiBuild && (
          <button
            type="button"
            className="sala-btn-outline text-xs tracking-wide"
            onClick={() => setAiOpen(true)}
            disabled={!canUseSubjectAi || aiLoading}
            title={
              canUseSubjectAi
                ? undefined
                : 'Abrí esta actividad desde Herramientas eligiendo una asignatura.'
            }
          >
            {aiLoading ? 'Generando…' : 'Construir con IA'}
          </button>
        )}
        {toolbarExtra}
        <button type="button" className="sala-btn-hero text-xs tracking-wide" onClick={onSave}>
          Guardar
        </button>
      </div>

      {error && (
        <div className="shrink-0 rounded-lg border border-red-200/60 bg-red-50/80 px-3 py-1.5 text-xs text-red-600 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      <div
        className={`studio-live-body studio-live-body--fluid flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200/80 bg-[var(--plugin-bg)] shadow-sm dark:border-[var(--sala-border-strong)]${embedded ? ' rounded-xl' : ''}${layout === 'split' ? ' studio-live-body--split' : ''}`}
      >
        <div className="studio-live-canvas flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
        {sidebar &&
          (sidebarCollapsed ? (
            <aside
              className={`studio-live-sidebar studio-live-sidebar--collapsed ${sidebarMinimal ? 'studio-live-sidebar--minimal' : ''}`}
            >
              <button
                type="button"
                className="studio-live-sidebar-toggle"
                onClick={() => setSidebarCollapsed(false)}
                title={sidebarTitle}
              >
                ☰
              </button>
            </aside>
          ) : (
            <aside className={`studio-live-sidebar ${sidebarMinimal ? 'studio-live-sidebar--minimal' : ''}`}>
              <div className="studio-live-sidebar-head">
                <span>{sidebarTitle}</span>
                <button
                  type="button"
                  className="studio-live-sidebar-toggle"
                  onClick={() => setSidebarCollapsed(true)}
                >
                  −
                </button>
              </div>
              <div className="studio-live-sidebar-scroll">{sidebar}</div>
            </aside>
          ))}
      </div>

      {resolvedSettings && (
        <StudioSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          title={settingsTitle}
          description={settingsDescription}
        >
          {resolvedSettings}
        </StudioSettingsDialog>
      )}

      {onAiBuild && (
        <StudioAiBuildDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          activityLabel={aiActivityLabel}
          subjectLabel={editorCtx?.subjectLabel}
          subjectReady={canUseSubjectAi}
          loading={aiLoading}
          onConfirm={handleAiConfirm}
        />
      )}
    </div>
  );
}
